// entrypoint.tsx

import { JSResourceForUserVisible } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import type { EntryPointRouteParams } from '@atlassian/jira-entry-points-plugin/src/common/types.tsx';
import parameters from '@atlassian/jira-relay/src/__generated__/FieldsPageViewQuery$parameters';
import { fg } from '@atlassian/jira-feature-gating';
import { JiraFieldConfigurationAri } from '@atlassian/ari/jira';

// Backwards compatibility with old URL params
const CustomFieldStatus = {
	TRASHED: 'trashed',
	ACTIVE: 'active',
} as const;

type InitialTab = 'active' | 'deleted';

export const globalSettingsIssueFieldsManagementRouteEntryPoint = createEntryPoint({
	root: JSResourceForUserVisible(
		() =>
			import(
				/* webpackChunkName: "async-jira-admin-pages-fields-page" */ './GlobalSettingsAdminPagesFields.tsx'
			),
	),
	getPreloadProps: ({
		tenantContext: { cloudId, activationId, isAdmin: isAdminOriginal },
		context: { query },
	}: EntryPointRouteParams) => {
		const sidebarId = query.sidebarId?.toUpperCase();
		const sidebarView = query.sidebarView?.toLowerCase();
		const fieldId = query.fieldId;
		const initialTab: InitialTab =
			query.customFieldStatus?.toLowerCase() === CustomFieldStatus.TRASHED ? 'deleted' : 'active';

		// Proxy for easier FG cleanup
		const isAdmin = fg('admin-pages-ui-block-if-not-admin') ? isAdminOriginal : true;

		const preloadFieldNodeId = fieldId
			? (() => {
					try {
						return JiraFieldConfigurationAri.create({
							fieldConfigurationId: fieldId,
							activationId,
							siteId: cloudId,
						}).toString();
					} catch (error) {
						return '';
					}
				})()
			: '';

		return {
			queries: {
				globalFields: {
					options: {
						fetchPolicy: 'network-only' as const,
					},
					parameters,
					variables: {
						cloudId,
						isAdmin,
						includeTypeDisplayName: fg('add_sort_by_field_type_display_name') || false,
						includeIsForge: fg('modify_forge_field_type_display_name') || false,
						preloadFieldNodeId,
						shouldPreloadField: !!preloadFieldNodeId,
					},
				},
			},
			extraProps: {
				initialTab,
				sidebarId,
				sidebarView,
				isAdmin,
				fieldNodeId: preloadFieldNodeId,
			},
		};
	},
});
